import { useState, useRef } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  seccionesAgendaService,
  SeccionAgenda,
  VarianteSeccion,
} from "@/services/seccionesAgendaService";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import FundadoraCard from '@/components/admin/FundadoraCard';
import CatalogoCard from '@/components/admin/CatalogoCard';
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { useToast } from "@/components/ui/use-toast";
import {
  Plus,
  Trash2,
  Eye,
  EyeOff,
  Pencil,
  ImageIcon,
  ChevronDown,
  ChevronUp,
  Layers,
} from "lucide-react";

const CATEGORIAS = [
  { value: "productivity", label: "Productividad" },
  { value: "wellness", label: "Bienestar" },
  { value: "creativity", label: "Creatividad" },
  { value: "finance", label: "Finanzas" },
  { value: "other", label: "Otros" },
] as const;

// ─────────────────────────────────────────────
// Formulario de sección (crear / editar)
// ─────────────────────────────────────────────
interface FormSeccionState {
  nombre: string;
  descripcion: string;
  categoria: string;
  paginas: string;
  precio: string;
  icon: string;
  imagen: File | null;
}

const FORM_INICIAL: FormSeccionState = {
  nombre: "",
  descripcion: "",
  categoria: "productivity",
  paginas: "0",
  precio: "0",
  icon: "📄",
  imagen: null,
};

function seccionToForm(s: SeccionAgenda): FormSeccionState {
  return {
    nombre: s.nombre,
    descripcion: s.descripcion,
    categoria: s.categoria,
    paginas: String(s.paginas),
    precio: String(s.precio),
    icon: s.icon,
    imagen: null,
  };
}

// ─────────────────────────────────────────────
// Formulario de variante
// ─────────────────────────────────────────────
interface FormVarianteState {
  slug: string;
  nombre: string;
  descripcion: string;
  imagen: File | null;
}

const VARIANTE_INICIAL: FormVarianteState = {
  slug: "",
  nombre: "",
  descripcion: "",
  imagen: null,
};

// ─────────────────────────────────────────────
// AdminSecciones
// ─────────────────────────────────────────────
export default function AdminSecciones() {
  const { toast } = useToast();
  const queryClient = useQueryClient();

  // UI state
  const [modalSeccion, setModalSeccion] = useState<"crear" | "editar" | null>(null);
  const [seccionEditando, setSeccionEditando] = useState<SeccionAgenda | null>(null);
  const [seccionExpandida, setSeccionExpandida] = useState<string | null>(null);
  const [confirmarEliminar, setConfirmarEliminar] = useState<SeccionAgenda | null>(null);
  const [confirmarEliminarVariante, setConfirmarEliminarVariante] = useState<{
    seccion: SeccionAgenda;
    variante: VarianteSeccion;
  } | null>(null);
  const [modalVariante, setModalVariante] = useState<{
    seccionId: string;
    modo: "crear" | "editar";
    variante?: VarianteSeccion;
  } | null>(null);

  // Form state
  const [form, setForm] = useState<FormSeccionState>(FORM_INICIAL);
  const [formVariante, setFormVariante] = useState<FormVarianteState>(VARIANTE_INICIAL);

  const imagenRef = useRef<HTMLInputElement>(null);
  const imagenVarianteRef = useRef<HTMLInputElement>(null);

  // ── Query ──────────────────────────────────
  const { data: secciones = [], isLoading } = useQuery({
    queryKey: ["secciones-agenda-admin"],
    queryFn: seccionesAgendaService.getSeccionesAdmin,
  });

  const invalidar = () => {
    queryClient.invalidateQueries({ queryKey: ["secciones-agenda-admin"] });
    queryClient.invalidateQueries({ queryKey: ["secciones-agenda"] });
  };

  // ── Mutations ─────────────────────────────
  const crearMutation = useMutation({
    mutationFn: () => {
      const fd = new FormData();
      fd.append("nombre", form.nombre);
      fd.append("descripcion", form.descripcion);
      fd.append("categoria", form.categoria);
      fd.append("paginas", form.paginas);
      fd.append("precio", form.precio);
      fd.append("icon", form.icon);
      if (form.imagen) fd.append("image", form.imagen);
      return seccionesAgendaService.crearSeccion(fd);
    },
    onSuccess: () => {
      toast({ title: "Sección creada", description: `"${form.nombre}" agregada exitosamente` });
      setModalSeccion(null);
      setForm(FORM_INICIAL);
      invalidar();
    },
    onError: () => toast({ title: "Error al crear sección", variant: "destructive" }),
  });

  const editarMutation = useMutation({
    mutationFn: () => {
      const fd = new FormData();
      fd.append("nombre", form.nombre);
      fd.append("descripcion", form.descripcion);
      fd.append("categoria", form.categoria);
      fd.append("paginas", form.paginas);
      fd.append("precio", form.precio);
      fd.append("icon", form.icon);
      if (form.imagen) fd.append("image", form.imagen);
      return seccionesAgendaService.actualizarSeccion(seccionEditando!.id, fd);
    },
    onSuccess: () => {
      toast({ title: "Sección actualizada" });
      setModalSeccion(null);
      setSeccionEditando(null);
      invalidar();
    },
    onError: () => toast({ title: "Error al actualizar sección", variant: "destructive" }),
  });

  const toggleActivoMutation = useMutation({
    mutationFn: ({ id, active }: { id: string; active: boolean }) => {
      const fd = new FormData();
      fd.append("active", String(active));
      return seccionesAgendaService.actualizarSeccion(id, fd);
    },
    onSuccess: invalidar,
    onError: () => toast({ title: "Error al actualizar", variant: "destructive" }),
  });

  const eliminarMutation = useMutation({
    mutationFn: (id: string) => seccionesAgendaService.eliminarSeccion(id),
    onSuccess: () => {
      toast({ title: "Sección eliminada" });
      setConfirmarEliminar(null);
      invalidar();
    },
    onError: () => toast({ title: "Error al eliminar", variant: "destructive" }),
  });

  const agregarVarianteMutation = useMutation({
    mutationFn: ({ seccionId }: { seccionId: string }) => {
      const fd = new FormData();
      fd.append("slug", formVariante.slug);
      fd.append("nombre", formVariante.nombre);
      fd.append("descripcion", formVariante.descripcion);
      if (formVariante.imagen) fd.append("image", formVariante.imagen);
      return seccionesAgendaService.agregarVariante(seccionId, fd);
    },
    onSuccess: () => {
      toast({ title: "Variante agregada" });
      setModalVariante(null);
      setFormVariante(VARIANTE_INICIAL);
      invalidar();
    },
    onError: () => toast({ title: "Error al agregar variante", variant: "destructive" }),
  });

  const editarVarianteMutation = useMutation({
    mutationFn: ({
      seccionId,
      varianteId,
    }: {
      seccionId: string;
      varianteId: string;
    }) => {
      const fd = new FormData();
      fd.append("nombre", formVariante.nombre);
      fd.append("descripcion", formVariante.descripcion);
      if (formVariante.imagen) fd.append("image", formVariante.imagen);
      return seccionesAgendaService.actualizarVariante(seccionId, varianteId, fd);
    },
    onSuccess: () => {
      toast({ title: "Variante actualizada" });
      setModalVariante(null);
      setFormVariante(VARIANTE_INICIAL);
      invalidar();
    },
    onError: () => toast({ title: "Error al actualizar variante", variant: "destructive" }),
  });

  const toggleVarianteMutation = useMutation({
    mutationFn: ({
      seccionId,
      varianteId,
      active,
    }: {
      seccionId: string;
      varianteId: string;
      active: boolean;
    }) => {
      const fd = new FormData();
      fd.append("active", String(active));
      return seccionesAgendaService.actualizarVariante(seccionId, varianteId, fd);
    },
    onSuccess: invalidar,
    onError: () => toast({ title: "Error al actualizar variante", variant: "destructive" }),
  });

  const eliminarVarianteMutation = useMutation({
    mutationFn: ({
      seccionId,
      varianteId,
    }: {
      seccionId: string;
      varianteId: string;
    }) => seccionesAgendaService.eliminarVariante(seccionId, varianteId),
    onSuccess: () => {
      toast({ title: "Variante eliminada" });
      setConfirmarEliminarVariante(null);
      invalidar();
    },
    onError: () => toast({ title: "Error al eliminar variante", variant: "destructive" }),
  });

  // ── Helpers UI ────────────────────────────
  function abrirCrear() {
    setForm(FORM_INICIAL);
    setSeccionEditando(null);
    setModalSeccion("crear");
  }

  function abrirEditar(s: SeccionAgenda) {
    setForm(seccionToForm(s));
    setSeccionEditando(s);
    setModalSeccion("editar");
  }

  function abrirAgregarVariante(seccionId: string) {
    setFormVariante(VARIANTE_INICIAL);
    setModalVariante({ seccionId, modo: "crear" });
  }

  function abrirEditarVariante(seccionId: string, v: VarianteSeccion) {
    setFormVariante({ slug: v.slug, nombre: v.nombre, descripcion: v.descripcion, imagen: null });
    setModalVariante({ seccionId, modo: "editar", variante: v });
  }

  function handleImagenChange(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0] ?? null;
    setForm((prev) => ({ ...prev, imagen: file }));
  }

  function handleImagenVarianteChange(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0] ?? null;
    setFormVariante((prev) => ({ ...prev, imagen: file }));
  }

  // ── Render ────────────────────────────────
  return (
    <div className="p-6 max-w-5xl mx-auto">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-playfair font-bold text-brown-dark flex items-center gap-2">
            <Layers className="w-6 h-6" />
            Secciones del CEO Planner
          </h1>
          <p className="text-sm text-brown-light/70 mt-1">
            Gestiona las secciones opcionales y sus variantes de plantilla
          </p>
        </div>
        <Button onClick={abrirCrear} className="gap-2">
          <Plus className="w-4 h-4" />
          Nueva Sección
        </Button>
      </div>

      {/* Foto de la fundadora (HU-09) */}
      <div className="mb-6">
        <FundadoraCard />
      </div>

      {/* Catálogo corporativo descargable (HU-14) */}
      <div className="mb-6">
        <CatalogoCard />
      </div>

      {/* Lista de secciones */}
      {isLoading ? (
        <div className="space-y-3">
          {[1, 2, 3].map((i) => (
            <Skeleton key={i} className="h-20 w-full rounded-lg" />
          ))}
        </div>
      ) : secciones.length === 0 ? (
        <Card className="text-center p-12 text-brown-light/60">
          <p>No hay secciones aún.</p>
          <p className="text-sm mt-1">Ejecutá el script de seed o creá una nueva sección.</p>
        </Card>
      ) : (
        <div className="space-y-3">
          {secciones.map((seccion) => (
            <Card
              key={seccion.id}
              className={`transition-all ${!seccion.active ? "opacity-50" : ""}`}
            >
              <CardContent className="p-4">
                {/* Fila principal */}
                <div className="flex items-center gap-3">
                  {/* Imagen preview */}
                  <div className="w-12 h-12 rounded-lg overflow-hidden bg-gray-100 flex-shrink-0">
                    {seccion.imageUrl ? (
                      <img
                        src={seccion.imageUrl}
                        alt={seccion.nombre}
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center text-2xl">
                        {seccion.icon}
                      </div>
                    )}
                  </div>

                  {/* Info */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-semibold text-brown-dark truncate">
                        {seccion.icon} {seccion.nombre}
                      </span>
                      <Badge variant="outline" className="text-xs">
                        {CATEGORIAS.find((c) => c.value === seccion.categoria)?.label}
                      </Badge>
                      <Badge variant="outline" className="text-xs">
                        {seccion.paginas} págs
                      </Badge>
                      <Badge variant="outline" className="text-xs">
                        ${seccion.precio}
                      </Badge>
                      {seccion.variantes.length > 0 && (
                        <Badge className="text-xs bg-primary/20 text-brown-dark">
                          {seccion.variantes.length} variantes
                        </Badge>
                      )}
                    </div>
                    <p className="text-xs text-brown-light/60 truncate mt-0.5">
                      {seccion.descripcion}
                    </p>
                  </div>

                  {/* Acciones */}
                  <div className="flex items-center gap-1 flex-shrink-0">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() =>
                        setSeccionExpandida(
                          seccionExpandida === seccion.id ? null : seccion.id
                        )
                      }
                      title="Ver variantes"
                    >
                      {seccionExpandida === seccion.id ? (
                        <ChevronUp className="w-4 h-4" />
                      ) : (
                        <ChevronDown className="w-4 h-4" />
                      )}
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() =>
                        toggleActivoMutation.mutate({ id: seccion.id, active: !seccion.active })
                      }
                      title={seccion.active ? "Desactivar" : "Activar"}
                    >
                      {seccion.active ? (
                        <Eye className="w-4 h-4 text-green-600" />
                      ) : (
                        <EyeOff className="w-4 h-4 text-gray-400" />
                      )}
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => abrirEditar(seccion)}
                      title="Editar"
                    >
                      <Pencil className="w-4 h-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => setConfirmarEliminar(seccion)}
                      title="Eliminar"
                    >
                      <Trash2 className="w-4 h-4 text-red-500" />
                    </Button>
                  </div>
                </div>

                {/* Variantes (expandido) */}
                {seccionExpandida === seccion.id && (
                  <div className="mt-4 border-t pt-4">
                    <div className="flex items-center justify-between mb-3">
                      <h4 className="text-sm font-semibold text-brown-dark">Variantes de plantilla</h4>
                      <Button
                        size="sm"
                        variant="outline"
                        className="gap-1 text-xs"
                        onClick={() => abrirAgregarVariante(seccion.id)}
                      >
                        <Plus className="w-3 h-3" />
                        Agregar variante
                      </Button>
                    </div>

                    {seccion.variantes.length === 0 ? (
                      <p className="text-xs text-brown-light/50 italic">
                        Sin variantes — el usuario no verá modal de selección para esta sección
                      </p>
                    ) : (
                      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
                        {seccion.variantes.map((v) => (
                          <div
                            key={v.id}
                            className={`rounded-lg border p-2 text-center relative group ${
                              !v.active ? "opacity-40" : ""
                            }`}
                          >
                            {v.imageUrl ? (
                              <img
                                src={v.imageUrl}
                                alt={v.nombre}
                                className="w-full aspect-[3/4] object-contain rounded mb-1"
                              />
                            ) : (
                              <div className="w-full aspect-[3/4] bg-gray-100 rounded mb-1 flex items-center justify-center">
                                <ImageIcon className="w-6 h-6 text-gray-300" />
                              </div>
                            )}
                            <p className="text-xs font-medium truncate">{v.nombre}</p>
                            <p className="text-xs text-gray-400 font-mono">{v.slug}</p>

                            {/* Acciones variante */}
                            <div className="absolute top-1 right-1 flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                              <button
                                className="bg-white rounded p-0.5 shadow border"
                                onClick={() =>
                                  toggleVarianteMutation.mutate({
                                    seccionId: seccion.id,
                                    varianteId: v.id,
                                    active: !v.active,
                                  })
                                }
                                title={v.active ? "Desactivar" : "Activar"}
                              >
                                {v.active ? (
                                  <Eye className="w-3 h-3 text-green-600" />
                                ) : (
                                  <EyeOff className="w-3 h-3 text-gray-400" />
                                )}
                              </button>
                              <button
                                className="bg-white rounded p-0.5 shadow border"
                                onClick={() => abrirEditarVariante(seccion.id, v)}
                                title="Editar"
                              >
                                <Pencil className="w-3 h-3" />
                              </button>
                              <button
                                className="bg-white rounded p-0.5 shadow border"
                                onClick={() =>
                                  setConfirmarEliminarVariante({ seccion, variante: v })
                                }
                                title="Eliminar"
                              >
                                <Trash2 className="w-3 h-3 text-red-500" />
                              </button>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* ── Modal crear/editar sección ── */}
      <Dialog open={modalSeccion !== null} onOpenChange={() => setModalSeccion(null)}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>
              {modalSeccion === "crear" ? "Nueva Sección" : `Editar: ${seccionEditando?.nombre}`}
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4 py-2">
            <div className="grid grid-cols-2 gap-4">
              <div className="col-span-2">
                <Label>Nombre *</Label>
                <Input
                  value={form.nombre}
                  onChange={(e) => setForm((p) => ({ ...p, nombre: e.target.value }))}
                  placeholder="Ej: Organización Diaria"
                />
              </div>

              <div className="col-span-2">
                <Label>Descripción</Label>
                <Textarea
                  value={form.descripcion}
                  onChange={(e) => setForm((p) => ({ ...p, descripcion: e.target.value }))}
                  placeholder="Descripción breve para el usuario"
                  rows={2}
                />
              </div>

              <div>
                <Label>Categoría</Label>
                <Select
                  value={form.categoria}
                  onValueChange={(v) => setForm((p) => ({ ...p, categoria: v }))}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {CATEGORIAS.map((c) => (
                      <SelectItem key={c.value} value={c.value}>
                        {c.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label>Icono (emoji)</Label>
                <Input
                  value={form.icon}
                  onChange={(e) => setForm((p) => ({ ...p, icon: e.target.value }))}
                  placeholder="📓"
                />
              </div>

              <div>
                <Label>Páginas</Label>
                <Input
                  type="number"
                  min="0"
                  value={form.paginas}
                  onChange={(e) => setForm((p) => ({ ...p, paginas: e.target.value }))}
                />
              </div>

              <div>
                <Label>Precio (USD)</Label>
                <Input
                  type="number"
                  min="0"
                  step="0.5"
                  value={form.precio}
                  onChange={(e) => setForm((p) => ({ ...p, precio: e.target.value }))}
                />
              </div>

              <div className="col-span-2">
                <Label>Imagen preview</Label>
                <div className="flex items-center gap-3 mt-1">
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => imagenRef.current?.click()}
                  >
                    <ImageIcon className="w-4 h-4 mr-2" />
                    {form.imagen ? form.imagen.name : "Seleccionar imagen"}
                  </Button>
                  {modalSeccion === "editar" && seccionEditando?.imageUrl && !form.imagen && (
                    <img
                      src={seccionEditando.imageUrl}
                      alt="actual"
                      className="h-12 w-12 object-cover rounded"
                    />
                  )}
                  {form.imagen && (
                    <img
                      src={URL.createObjectURL(form.imagen)}
                      alt="preview"
                      className="h-12 w-12 object-cover rounded"
                    />
                  )}
                  <input
                    ref={imagenRef}
                    type="file"
                    accept="image/*"
                    className="hidden"
                    onChange={handleImagenChange}
                  />
                </div>
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setModalSeccion(null)}>
              Cancelar
            </Button>
            <Button
              onClick={() =>
                modalSeccion === "crear" ? crearMutation.mutate() : editarMutation.mutate()
              }
              disabled={!form.nombre || crearMutation.isPending || editarMutation.isPending}
            >
              {crearMutation.isPending || editarMutation.isPending
                ? "Guardando..."
                : "Guardar"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ── Modal agregar/editar variante ── */}
      <Dialog open={modalVariante !== null} onOpenChange={() => setModalVariante(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>
              {modalVariante?.modo === "crear" ? "Agregar Variante" : "Editar Variante"}
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4 py-2">
            {modalVariante?.modo === "crear" && (
              <div>
                <Label>Slug * (identificador único, sin espacios)</Label>
                <Input
                  value={formVariante.slug}
                  onChange={(e) =>
                    setFormVariante((p) => ({
                      ...p,
                      slug: e.target.value.toLowerCase().replace(/\s+/g, "-"),
                    }))
                  }
                  placeholder="ej: sin-calendario"
                />
              </div>
            )}

            <div>
              <Label>Nombre *</Label>
              <Input
                value={formVariante.nombre}
                onChange={(e) => setFormVariante((p) => ({ ...p, nombre: e.target.value }))}
                placeholder="Ej: Día Simple"
              />
            </div>

            <div>
              <Label>Descripción</Label>
              <Textarea
                value={formVariante.descripcion}
                onChange={(e) => setFormVariante((p) => ({ ...p, descripcion: e.target.value }))}
                placeholder="Descripción breve"
                rows={2}
              />
            </div>

            <div>
              <Label>Imagen de preview *</Label>
              <div className="flex items-center gap-3 mt-1">
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => imagenVarianteRef.current?.click()}
                >
                  <ImageIcon className="w-4 h-4 mr-2" />
                  {formVariante.imagen ? formVariante.imagen.name : "Seleccionar imagen"}
                </Button>
                {modalVariante?.modo === "editar" &&
                  modalVariante.variante?.imageUrl &&
                  !formVariante.imagen && (
                    <img
                      src={modalVariante.variante.imageUrl}
                      alt="actual"
                      className="h-16 w-12 object-cover rounded"
                    />
                  )}
                {formVariante.imagen && (
                  <img
                    src={URL.createObjectURL(formVariante.imagen)}
                    alt="preview"
                    className="h-16 w-12 object-cover rounded"
                  />
                )}
                <input
                  ref={imagenVarianteRef}
                  type="file"
                  accept="image/*"
                  className="hidden"
                  onChange={handleImagenVarianteChange}
                />
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setModalVariante(null)}>
              Cancelar
            </Button>
            <Button
              onClick={() => {
                if (!modalVariante) return;
                if (modalVariante.modo === "crear") {
                  agregarVarianteMutation.mutate({ seccionId: modalVariante.seccionId });
                } else {
                  editarVarianteMutation.mutate({
                    seccionId: modalVariante.seccionId,
                    varianteId: modalVariante.variante!.id,
                  });
                }
              }}
              disabled={
                !formVariante.nombre ||
                (modalVariante?.modo === "crear" && !formVariante.slug) ||
                agregarVarianteMutation.isPending ||
                editarVarianteMutation.isPending
              }
            >
              {agregarVarianteMutation.isPending || editarVarianteMutation.isPending
                ? "Guardando..."
                : "Guardar"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ── Confirmar eliminar sección ── */}
      <AlertDialog
        open={confirmarEliminar !== null}
        onOpenChange={() => setConfirmarEliminar(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>¿Eliminar sección?</AlertDialogTitle>
            <AlertDialogDescription>
              Vas a eliminar <strong>"{confirmarEliminar?.nombre}"</strong> con todas sus variantes.
              Esta acción no se puede deshacer.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              className="bg-red-600 hover:bg-red-700"
              onClick={() => eliminarMutation.mutate(confirmarEliminar!.id)}
            >
              Eliminar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* ── Confirmar eliminar variante ── */}
      <AlertDialog
        open={confirmarEliminarVariante !== null}
        onOpenChange={() => setConfirmarEliminarVariante(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>¿Eliminar variante?</AlertDialogTitle>
            <AlertDialogDescription>
              Vas a eliminar la variante{" "}
              <strong>"{confirmarEliminarVariante?.variante.nombre}"</strong>. Esta acción no se
              puede deshacer.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              className="bg-red-600 hover:bg-red-700"
              onClick={() =>
                eliminarVarianteMutation.mutate({
                  seccionId: confirmarEliminarVariante!.seccion.id,
                  varianteId: confirmarEliminarVariante!.variante.id,
                })
              }
            >
              Eliminar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
